import api from './api'

const trustListService = {
  // ============================================================
  // Owner operations - Create and manage own lists
  // ============================================================

  /**
   * Get all trust lists owned by profile
   * @param {number} profileId - Profile ID
   */
  getMyLists: async (profileId) => {
    const response = await api.get(`/profiles/${profileId}/lists`)
    return response.data
  },

  /**
   * Create a new trust list
   * @param {number} profileId - Profile ID
   * @param {Object} data - List data
   * @param {string} data.name - List name
   * @param {string} [data.description] - List description
   * @param {boolean} [data.is_public=true] - Whether list is visible to trusters
   * @param {number[]} [data.category_ids] - Initial category IDs to add
   */
  createList: async (profileId, data) => {
    const response = await api.post(`/profiles/${profileId}/lists`, data)
    return response.data
  },

  /**
   * Get a specific trust list
   * @param {number} profileId - Profile ID
   * @param {number} listId - List ID
   */
  getList: async (profileId, listId) => {
    const response = await api.get(`/profiles/${profileId}/lists/${listId}`)
    return response.data
  },

  /**
   * Update a trust list
   * @param {number} profileId - Profile ID
   * @param {number} listId - List ID
   * @param {Object} data - Updated list data
   */
  updateList: async (profileId, listId, data) => {
    const response = await api.put(`/profiles/${profileId}/lists/${listId}`, data)
    return response.data
  },

  /**
   * Delete a trust list
   * @param {number} profileId - Profile ID
   * @param {number} listId - List ID
   */
  deleteList: async (profileId, listId) => {
    const response = await api.delete(`/profiles/${profileId}/lists/${listId}`)
    return response.data
  },

  /**
   * Add items to a trust list
   * @param {number} profileId - Profile ID
   * @param {number} listId - List ID
   * @param {number[]} categoryIds - Category IDs to add
   * @param {number} [suggestedTrust] - Suggested trust value for adopters
   */
  addItemsToList: async (profileId, listId, categoryIds, suggestedTrust = null) => {
    const response = await api.post(`/profiles/${profileId}/lists/${listId}/items`, {
      category_ids: categoryIds,
      suggested_trust: suggestedTrust
    })
    return response.data
  },

  /**
   * Remove item from a trust list
   * @param {number} profileId - Profile ID
   * @param {number} listId - List ID
   * @param {number} itemId - Item ID to remove
   */
  removeItemFromList: async (profileId, listId, itemId) => {
    const response = await api.delete(`/profiles/${profileId}/lists/${listId}/items/${itemId}`)
    return response.data
  },

  // ============================================================
  // Discovery operations - Browse and adopt lists from trusted profiles
  // ============================================================

  /**
   * Get trusted profiles that have public lists
   * @param {number} profileId - Profile ID
   */
  getTrustedProfilesWithLists: async (profileId) => {
    const response = await api.get(`/profiles/${profileId}/trusted-profiles-with-lists`)
    return response.data
  },

  /**
   * Get available lists from a trusted profile
   * @param {number} profileId - Your profile ID
   * @param {number} trustedProfileId - Trusted profile's ID
   */
  getAvailableLists: async (profileId, trustedProfileId) => {
    const response = await api.get(`/profiles/${profileId}/trusted/${trustedProfileId}/lists`)
    return response.data
  },

  /**
   * Adopt (copy) items from a trust list
   * @param {number} profileId - Your profile ID
   * @param {Object} data - Adoption data
   * @param {number} data.list_id - List ID to adopt from
   * @param {number[]} [data.item_ids] - Specific item IDs to adopt
   * @param {boolean} [data.adopt_all=false] - Adopt all items
   * @param {number} [data.trust_override] - Override trust value
   * @param {string} [data.duplicate_handling='skip'] - 'skip' | 'update'
   */
  adoptListItems: async (profileId, data) => {
    const response = await api.post(`/profiles/${profileId}/adopt`, data)
    return response.data
  },

  // ============================================================
  // Copy trust between profiles (same account)
  // ============================================================

  /**
   * Copy trust relationships from one profile to another
   * @param {number} targetProfileId - Profile to copy TO
   * @param {number} sourceProfileId - Profile to copy FROM
   * @param {Object} data - Copy options
   * @param {number[]} [data.category_ids] - Specific category IDs to copy
   * @param {boolean} [data.copy_all=false] - Copy all relationships
   * @param {string} [data.duplicate_handling='skip'] - 'skip' | 'update'
   */
  copyTrustRelationships: async (targetProfileId, sourceProfileId, data) => {
    const response = await api.post(
      `/profiles/${targetProfileId}/trust/copy-from/${sourceProfileId}`,
      data
    )
    return response.data
  }
}

export default trustListService
